<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay, useTheme } from 'vuetify'
import { useDialogStore } from '@/stores/dialog'
import { useUserStore } from '@/stores/user'
import { useEventUserRolesStore } from '@/stores/eventUserRoles'
import { useTransactionsStore } from '@/stores/transactions'
import { useAuth } from '@/composables/useAuth'
import { clearStoredAuthTokens } from '@/services/authClient'
import BaseSnackbar from '@/components/ui/BaseSnackbar.vue'
import BaseDialog from '@/components/ui/BaseDialog.vue'

const route = useRoute()
const router = useRouter()
const { smAndDown } = useDisplay()
const theme = useTheme()
const dialogStore = useDialogStore()
const userStore = useUserStore()
const eventUserRolesStore = useEventUserRolesStore()
const transactionsStore = useTransactionsStore()
const auth = useAuth()

const authReady = ref(false)
const darkMode = ref(false)

// Get stored user state for instant display before auth loads
const storedInitials = localStorage.getItem('userInitials') || ''
const storedHasEventsNav = localStorage.getItem('hasEventsNav') === 'true'
const storedHasAdminNav = localStorage.getItem('hasAdminNav') === 'true'

onMounted(async () => {
  // Load dark mode preference
  const savedDark = localStorage.getItem('dark')
  if (savedDark === 'true') {
    darkMode.value = true
    theme.change('dark')
  } else if (savedDark === 'false') {
    darkMode.value = false
    theme.change('light')
  } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    darkMode.value = true
    theme.change('dark')
  }
  // Set a fallback timeout in case auth initialization hangs
  const authTimeout = setTimeout(() => {
    if (!authReady.value) {
      console.warn('[Auth] Fallback timeout reached - showing UI')
      authReady.value = true
    }
  }, 5000)

  try {
    await auth.initialize()
  } catch (e) {
    console.error('Auth init failed:', e)
  } finally {
    clearTimeout(authTimeout)
    authReady.value = true
  }

  // Load user data if logged in
  if (userStore.token) {
    transactionsStore.loadTransactions()
    await eventUserRolesStore.loadEventUserRoles()
  }

  // Save nav visibility flags to localStorage to prevent layout shift on next load
  updateNavStorageFlags()

  // Listen for auth events
  window.addEventListener('auth:token-refreshed', handleTokenRefreshed as EventListener)
  window.addEventListener('auth:session-expired', handleSessionExpired)
})

function handleTokenRefreshed(event: CustomEvent<string>) {
  // Update store with refreshed token
  userStore.setLogin(event.detail)
}

function handleSessionExpired() {
  clearStoredAuthTokens()
  userStore.logout()
  clearNavStorageFlags()
  if (route.meta.requiresAuth) {
    router.push('/')
  }
}

function updateNavStorageFlags() {
  if (userStore.token) {
    localStorage.setItem('hasEventsNav', 'true')
  } else {
    localStorage.removeItem('hasEventsNav')
  }

  if (userStore.isAdmin || eventUserRolesStore.eventRoles.some((x) => x.role === 'ADMIN')) {
    localStorage.setItem('hasAdminNav', 'true')
  } else {
    localStorage.removeItem('hasAdminNav')
  }
}

function clearNavStorageFlags() {
  localStorage.removeItem('hasEventsNav')
  localStorage.removeItem('hasAdminNav')
}

onUnmounted(() => {
  window.removeEventListener('auth:token-refreshed', handleTokenRefreshed as EventListener)
  window.removeEventListener('auth:session-expired', handleSessionExpired)
})

const externalLinks = [
  { href: 'https://copte.fr', label: 'Diocèse', icon: 'mdi-church' },
  { href: 'https://biblio.copte.fr', label: 'BiblioCopte', icon: 'mdi-book-open-variant' },
  { href: 'https://www.chantscoptes.net', label: 'ChantsCoptes', icon: 'mdi-music' },
  { href: 'https://jeunes.copte.fr', label: 'Jeunes Coptes', icon: 'mdi-account-group' },
  { href: 'https://katameros.app', label: 'Lectures', icon: 'mdi-book-cross' },
]

const navItems = computed(() => {
  const items: { link: string; text: string; mobileText: string; icon: string }[] = [
    { link: '/', text: 'Accueil', mobileText: 'Accueil', icon: 'mdi-home' },
  ]

  // Show "Mes événements" if logged in OR if stored flag indicates user was logged in (prevents layout shift)
  const showEventsNav = userStore.token || (!authReady.value && storedHasEventsNav)
  if (showEventsNav) {
    items.push({
      link: '/mes-evenements',
      text: 'Mes événements',
      mobileText: 'Réservations',
      icon: 'mdi-calendar',
    })
  }

  // Show "Administration" if admin OR if stored flag indicates user was admin (prevents layout shift)
  const showAdminNav = userStore.isAdmin || eventUserRolesStore.eventRoles.some((x) => x.role === 'ADMIN') || (!authReady.value && storedHasAdminNav)
  if (showAdminNav) {
    items.push({
      link: '/admin',
      text: 'Administration',
      mobileText: 'Admin',
      icon: 'mdi-account-key',
    })
  }

  return items
})

const initials = computed(() => {
  const firstName = userStore.firstName
  const lastName = userStore.lastName
  if (firstName && lastName) {
    return firstName.charAt(0) + lastName.charAt(0)
  }
  return ''
})

function logout() {
  userStore.logout()
  auth.logout()
  if (route.meta.requiresAuth || route.meta.requiresAdmin) {
    router.push('/')
  }
}

function editProfile() {
  router.push({ name: 'profile' })
}

function onDarkModeChange() {
  theme.change(darkMode.value ? 'dark' : 'light')
  localStorage.setItem('dark', darkMode.value ? 'true' : 'false')
}
</script>

<template>
  <v-app>
    <!-- App Bar / Navbar -->
    <v-app-bar class="app-navbar" elevation="0">
      <router-link
        to="/"
        class="navbar-brand"
      >
        <img
          :src="darkMode ? '/LogoCircleWhite.png' : '/logo.png'"
          alt="DCO"
          class="navbar-logo"
        >
        <span class="navbar-title">Event DCO</span>
      </router-link>

      <v-spacer />

      <nav class="navbar-nav">
        <!-- Desktop Navigation -->
        <template v-if="!smAndDown">
          <router-link
            v-for="item in navItems"
            :key="item.link"
            :to="item.link"
            class="nav-link"
          >
            <v-icon size="18" class="nav-link-icon">{{ item.icon }}</v-icon>
            <span>{{ item.text }}</span>
          </router-link>
        </template>

        <!-- External Links Menu (all breakpoints) -->
        <v-menu offset="8" location="bottom end">
          <template #activator="{ props }">
            <button class="nav-link" type="button" v-bind="props">
              <span>Liens</span>
              <v-icon size="16" class="nav-link-chevron">mdi-chevron-down</v-icon>
            </button>
          </template>
          <div class="menu-card">
            <a
              v-for="link in externalLinks"
              :key="link.href"
              :href="link.href"
              target="_blank"
              rel="noopener"
              class="menu-item"
            >
              <v-icon size="20" class="menu-item-icon">{{ link.icon }}</v-icon>
              <span>{{ link.label }}</span>
            </a>
          </div>
        </v-menu>

        <!-- Loading: Show avatar with stored initials if user was logged in -->
        <button
          v-if="!authReady && storedInitials"
          class="avatar-btn"
          style="pointer-events: none;"
          type="button"
        >
          <span class="avatar-initials">{{ storedInitials }}</span>
        </button>

        <!-- User Menu (Logged In) -->
        <v-menu
          v-else-if="authReady && auth.isAuthenticated.value"
          offset="8"
          location="bottom end"
        >
          <template #activator="{ props }">
            <button
              class="avatar-btn"
              type="button"
              v-bind="props"
            >
              <span class="avatar-initials">{{ initials }}</span>
            </button>
          </template>
          <div class="user-menu">
            <div class="user-menu-header">
              <div class="user-menu-avatar">{{ initials }}</div>
              <div class="user-menu-identity">
                <div class="user-menu-name">{{ userStore.name }}</div>
                <div class="user-menu-email">{{ userStore.email }}</div>
              </div>
            </div>
            <div class="user-menu-divider" />
            <button type="button" class="user-menu-item" @click="editProfile">
              <v-icon size="20" class="user-menu-item-icon">mdi-account-circle-outline</v-icon>
              <span>Profil</span>
            </button>
            <div class="user-menu-item user-menu-item--static" @click.stop>
              <v-icon size="20" class="user-menu-item-icon">mdi-theme-light-dark</v-icon>
              <span class="flex-grow-1">Mode sombre</span>
              <v-switch
                v-model="darkMode"
                color="primary"
                inset
                hide-details
                density="compact"
                class="user-menu-switch"
                @update:model-value="onDarkModeChange"
              />
            </div>
            <div class="user-menu-divider" />
            <button type="button" class="user-menu-item user-menu-item--danger" @click="logout">
              <v-icon size="20" class="user-menu-item-icon">mdi-logout</v-icon>
              <span>Se déconnecter</span>
            </button>
          </div>
        </v-menu>

        <!-- Login Button (Not Logged In) -->
        <button
          v-else-if="authReady"
          class="login-btn"
          type="button"
          @click="auth.login()"
        >
          <v-icon size="18">mdi-account</v-icon>
          <span>Connexion</span>
        </button>
      </nav>
    </v-app-bar>

    <!-- Main Content -->
    <v-main class="bg-background">
      <router-view :key="route.fullPath" />

      <!-- Dialog -->
      <BaseDialog
        v-model="dialogStore.status"
        :text="dialogStore.text"
        :callback="dialogStore.callback"
      />

      <!-- Snackbar -->
      <BaseSnackbar
        v-model="dialogStore.snackbar"
        :text="dialogStore.snackbarText"
        :color="dialogStore.snackbarColor"
      />
    </v-main>

    <!-- Footer -->
    <footer class="app-footer">
      <div class="footer-content">
        <div class="footer-links">
          <a
            href="https://copte.fr"
            target="_blank"
            rel="noopener"
          >Diocèse</a>
          <a
            href="https://biblio.copte.fr"
            target="_blank"
            rel="noopener"
          >BiblioCopte</a>
          <a
            href="https://www.chantscoptes.net"
            target="_blank"
            rel="noopener"
          >ChantsCoptes</a>
          <a
            href="https://jeunes.copte.fr"
            target="_blank"
            rel="noopener"
          >Jeunes Coptes</a>
          <a
            href="https://copte.fr/contact"
            target="_blank"
            rel="noopener"
          >Contact</a>
          <a
            href="https://katameros.app"
            target="_blank"
            rel="noopener"
          >Lectures</a>
        </div>
        <div class="site-other">
          <a
            href="https://www.facebook.com/DCO-Anba-Marc-491894440960099"
            target="_blank"
            rel="noopener"
            aria-label="Facebook"
          >
            <svg class="inline-block" viewBox="0 0 24 24" width="20" height="20">
              <path fill="currentColor" d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z" />
            </svg>
          </a>
          <a
            href="https://www.instagram.com/dcoanbamarc"
            target="_blank"
            rel="noopener"
            aria-label="Instagram"
          >
            <v-icon size="20">mdi-instagram</v-icon>
          </a>
          <a
            href="https://www.youtube.com/c/DCOAnbaMarc"
            target="_blank"
            rel="noopener"
            aria-label="YouTube"
          >
            <v-icon size="20">mdi-youtube</v-icon>
          </a>
          <a
            href="https://soundcloud.com/dcoanbamarc"
            target="_blank"
            rel="noopener"
            aria-label="SoundCloud"
          >
            <v-icon size="20">mdi-soundcloud</v-icon>
          </a>
        </div>
        <div class="footer-copyright">
          &copy; {{ new Date().getFullYear() }} Diocèse Copte Orthodoxe
        </div>
      </div>
    </footer>

    <!-- Bottom Navigation (Mobile Only) -->
    <nav
      v-if="smAndDown && navItems.length > 1"
      class="mobile-nav"
    >
      <router-link
        v-for="item in navItems"
        :key="item.link"
        :to="item.link"
        class="mobile-nav-item"
      >
        <span class="mobile-nav-indicator">
          <v-icon size="22">{{ item.icon }}</v-icon>
        </span>
        <span class="mobile-nav-label">{{ item.mobileText }}</span>
      </router-link>
    </nav>
  </v-app>
</template>

<style>
@font-face {
  font-family: 'DCO';
  src: url('/DCO-Regular.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}

html {
  overflow-y: auto !important;
}

.app-navbar {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)) !important;
  backdrop-filter: saturate(180%) blur(14px);
  -webkit-backdrop-filter: saturate(180%) blur(14px);
  background: color-mix(in srgb, rgb(var(--v-theme-surface)) 85%, transparent) !important;
}

.navbar-brand {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 10px;
  color: inherit;
  text-decoration: none;
  transition: background 120ms ease;
}

.navbar-brand:hover {
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.navbar-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 6px;
}

.nav-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  color: rgb(var(--v-theme-on-surface));
  background: transparent;
  border: none;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.2px;
  text-decoration: none;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;
  white-space: nowrap;
}

.nav-link:hover {
  background: rgba(var(--v-theme-on-surface), 0.06);
}

.nav-link.router-link-exact-active {
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.08);
}

.nav-link-icon {
  opacity: 0.8;
}

.nav-link-chevron {
  opacity: 0.6;
  margin-left: 2px;
}

.login-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, color-mix(in srgb, rgb(var(--v-theme-primary)) 75%, #000 0%) 100%);
  color: rgb(var(--v-theme-on-primary));
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  transition: transform 140ms ease, box-shadow 140ms ease;
  margin-left: 6px;
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}

.menu-card {
  min-width: 220px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 14px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
  overflow: hidden;
  padding: 8px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  color: rgb(var(--v-theme-on-surface));
  font-size: 0.9rem;
  text-decoration: none;
  text-align: left;
  border-radius: 8px;
  cursor: pointer;
  transition: background 120ms ease;
}

.menu-item:hover {
  background: rgba(var(--v-theme-on-surface), 0.06);
}

.menu-item-icon {
  flex-shrink: 0;
  opacity: 0.75;
}

.mobile-nav {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  display: flex;
  gap: 4px;
  padding: 6px;
  border-radius: 20px;
  background: color-mix(in srgb, rgb(var(--v-theme-surface)) 92%, transparent);
  backdrop-filter: saturate(180%) blur(16px);
  -webkit-backdrop-filter: saturate(180%) blur(16px);
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  z-index: 5;
}

.mobile-nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 6px 6px;
  border-radius: 14px;
  color: rgba(var(--v-theme-on-surface), 0.7);
  text-decoration: none;
  transition: color 120ms ease;
}

.mobile-nav-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 28px;
  border-radius: 14px;
  transition: background 160ms ease, transform 160ms ease;
}

.mobile-nav-item:hover .mobile-nav-indicator {
  background: rgba(var(--v-theme-on-surface), 0.06);
}

.mobile-nav-label {
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.2px;
  white-space: nowrap;
}

.mobile-nav-item.router-link-exact-active {
  color: rgb(var(--v-theme-primary));
}

.mobile-nav-item.router-link-exact-active .mobile-nav-indicator {
  background: rgba(var(--v-theme-primary), 0.16);
}

/* Reserve space so fixed mobile-nav doesn't overlap content. */
@media (max-width: 959px) {
  .v-main {
    padding-bottom: calc(84px + env(safe-area-inset-bottom, 0px)) !important;
  }
}

.avatar-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, color-mix(in srgb, rgb(var(--v-theme-primary)) 70%, #000 0%) 100%);
  color: rgb(var(--v-theme-on-primary));
  border: 2px solid rgba(var(--v-theme-surface), 0.9);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
  margin: 0 8px;
  padding: 0;
}

.avatar-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  border-color: rgba(var(--v-theme-primary), 0.25);
}

.avatar-btn:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.avatar-initials {
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.user-menu {
  min-width: 260px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 14px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
  overflow: hidden;
  padding: 8px;
}

.user-menu-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 10px 12px;
}

.user-menu-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, color-mix(in srgb, rgb(var(--v-theme-primary)) 70%, #000 0%) 100%);
  color: rgb(var(--v-theme-on-primary));
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.95rem;
  text-transform: uppercase;
  flex-shrink: 0;
}

.user-menu-identity {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.user-menu-name {
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-menu-email {
  font-size: 0.8rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-menu-divider {
  height: 1px;
  background: rgba(var(--v-border-color), var(--v-border-opacity));
  margin: 4px 0;
}

.user-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  color: rgb(var(--v-theme-on-surface));
  font-size: 0.9rem;
  text-align: left;
  border-radius: 8px;
  cursor: pointer;
  transition: background 120ms ease;
}

.user-menu-item:hover {
  background: rgba(var(--v-theme-on-surface), 0.06);
}

.user-menu-item--static {
  cursor: default;
}

.user-menu-item--static:hover {
  background: transparent;
}

.user-menu-item--danger {
  color: rgb(var(--v-theme-error));
}

.user-menu-item--danger:hover {
  background: rgba(var(--v-theme-error), 0.08);
}

.user-menu-item-icon {
  flex-shrink: 0;
  opacity: 0.75;
}

.user-menu-switch {
  flex-shrink: 0;
  margin: 0;
}

.user-menu-switch :deep(.v-selection-control) {
  min-height: 0;
}

.v-toolbar a.text-decoration-none {
  color: inherit !important;
}

.navbar-logo {
  height: 36px;
  width: auto;
  margin: 0 8px;
}

.navbar-title {
  font-family: 'DCO', serif;
  font-size: 1.4rem;
  font-weight: normal;
  letter-spacing: 0.5px;
}

.app-footer {
  background: rgba(var(--v-theme-on-surface), 0.03);
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding: 24px 16px;
}

.footer-content {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.footer-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 20px;
  margin-bottom: 16px;
}

.footer-links a {
  color: rgba(var(--v-theme-on-surface), 0.7);
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.15s;
}

.footer-links a:hover {
  color: rgb(var(--v-theme-primary));
}

.site-other {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 16px;
}

.site-other a {
  color: rgba(var(--v-theme-on-surface), 0.5);
  transition: color 0.15s;
}

.site-other a:hover {
  color: rgb(var(--v-theme-primary));
}

.footer-copyright {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.5);
}

@media (max-width: 959px) {
  .app-footer {
    padding-bottom: 72px; /* Space for bottom navigation */
  }
}
</style>
